// NotesSection.js - Complete with Create functionality
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Card, Button, Form, Alert } from 'react-bootstrap';
import Icon from '../../../../components/icon/Icon';
import AuthContext from '../../../../contexts/authContext';
import { putAPI, deleteAPI, postAPI } from '../../../../apis/backend/apiNode';
import showNotification from '../../../../components/extras/showNotification';
import ConfirmationPopup from '../../../../components/PlanningCalendar/ConfirmationPopup';
import AccessContext from 'contexts/accessContext';

const NotesSection = ({
  notes,
  formatDateTime,
  getUserDisplayName,
  interventionId,
  onNotesUpdate,
  reloadData
}) => {
  const { userData } = useContext(AuthContext);
  const { access } = useContext(AccessContext);

  const isTechnician = userData?.function?.name?.includes('Technicien')
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const textareaRef = useRef(null);

  // Debug useEffect
  useEffect(() => {
    console.log('🔍 NotesSection Debug:');
    console.log('- Intervention ID:', interventionId);
    console.log('- Notes:', notes);
    console.log('- User ID:', userData?._id);
  }, [interventionId, notes, userData]);

  // Vérifier si l'utilisateur peut modifier la note
  const canEditNote = (note) => {
    if (!userData || !note?.createdBy) return false;


    const currentUserId = userData._id || userData.id;
    let noteCreatorId;

    if (note.createdBy && typeof note.createdBy === 'object') {
      noteCreatorId = note.createdBy._id || note.createdBy.id;
    } else if (typeof note.createdBy === 'string') {
      noteCreatorId = note.createdBy;
    }

    // ⚠️ Retourner false si noteCreatorId est encore undefined
    if (!noteCreatorId) return false;

    return currentUserId.toString() === noteCreatorId.toString() || access?.includes?.("intervention:can_update") || userData?.role === "sup_admin"
      ;
  };


  // Focus on textarea when creating
  useEffect(() => {
    if (isCreating && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isCreating]);

  // Démarrer l'édition
  const handleEditClick = (note) => {
    setEditingNote(note);
    setEditText(note.note || '');
  };

  // Démarrer la création d'une nouvelle note
  const handleStartCreate = () => {
    setIsCreating(true);
    setNewNoteText('');
  };

  // Annuler la création
  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewNoteText('');
  };

  // Sauvegarder une nouvelle note - UPDATED ENDPOINT
  const handleSaveNewNote = async () => {
    if (!newNoteText.trim()) {
      showNotification('Attention', 'La note ne peut pas être vide', 'warning');
      return;
    }

    if (!interventionId) {
      console.error('❌ interventionId is undefined!');
      showNotification('Erreur', 'ID d\'intervention manquant', 'danger');
      return;
    }

    setLoading(true);
    try {
      console.log('➕ Creating new note:', {
        interventionId,
        text: newNoteText,
        createdBy: userData._id
      });

      // IMPORTANT: Use PLURAL "interventions" to match backend route
      const endpoint = `/intervention/${interventionId}/note`;
      console.log('API Endpoint:', endpoint);

      const response = await postAPI('planning', {
        note: newNoteText.trim(),  // ✅ matches backend validation
        createdBy: userData._id || userData.id,
        type: 'note',
        priority: 'normal'
      }, endpoint);

      console.log('➕ Create Note Response:', response);

      // Better response handling
      if (response === null || response === undefined) {
        throw new Error('La réponse de l\'API est vide. Vérifiez l\'endpoint et les logs du serveur.');
      }

      if (response.success) {
        showNotification('Succès', 'Note créée avec succès', 'success');
        reloadData();

        // Update notes locally with the new note
        const newNote = response.data?.note || {
          _id: Date.now().toString(), // temporary ID
          note: newNoteText,
          createdBy: userData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const updatedNotes = [newNote, ...notes];

        if (onNotesUpdate) {
          onNotesUpdate(updatedNotes);
        }

        setIsCreating(false);
        setNewNoteText('');
      } else {
        throw new Error(response.error || response.message || response.errors?.[0]?.msg || 'Échec de la création');
      }
    } catch (error) {
      console.error('❌ Create error:', error);
      showNotification('Erreur', error.message || 'Échec de la création de la note', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder la modification - UPDATED ENDPOINT
  const handleSaveEdit = async () => {
    if (!editingNote || !editText.trim()) {
      console.error('❌ Missing editingNote or editText');
      return;
    }

    if (!interventionId) {
      console.error('❌ interventionId is undefined!');
      showNotification('Erreur', 'ID d\'intervention manquant', 'danger');
      return;
    }

    setLoading(true);
    try {
      console.log('💾 Saving note:', {
        interventionId,
        noteId: editingNote._id,
        newText: editText,
        updatedBy: userData._id
      });

      // IMPORTANT: Use PLURAL "interventions" to match backend route
      const endpoint = `/intervention/${interventionId}/note`;
      console.log('API Endpoint:', endpoint);

      const response = await putAPI('planning', {
        noteId: editingNote._id,
        newText: editText,
        updatedBy: userData._id || userData.id
      }, endpoint);

      console.log('📝 API Response:', response);

      // Better response handling
      if (response === null || response === undefined) {
        throw new Error('La réponse de l\'API est vide. Vérifiez l\'endpoint et les logs du serveur.');
      }

      if (response.success) {
        showNotification('Succès', 'Note mise à jour avec succès', 'success');
        reloadData();
        // Update notes locally
        const updatedNotes = notes.map(note => {
          if (note._id === editingNote._id) {
            return {
              ...note,
              note: editText,
              updatedAt: new Date().toISOString()
            };
          }
          return note;
        });

        if (onNotesUpdate) {
          onNotesUpdate(updatedNotes);
        }

        setEditingNote(null);
        setEditText('');
      } else {
        throw new Error(response.error || response.message || response.errors?.[0]?.msg || 'Échec de la mise à jour');
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      showNotification('Erreur', error.message || 'Échec de la mise à jour de la note', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une note - UPDATED ENDPOINT
  const handleDeleteNote = async () => {
    if (!noteToDelete || !interventionId) {
      console.error('❌ Missing note or intervention ID');
      return;
    }

    setLoading(true);
    try {
      console.log('🗑️ Deleting note:', {
        interventionId,
        noteId: noteToDelete._id,
        deletedBy: userData._id
      });

      // IMPORTANT: Use PLURAL "interventions" to match backend route
      const endpoint = `/intervention/${interventionId}/note/${noteToDelete._id}`;
      console.log('API Endpoint:', endpoint);

      const response = await deleteAPI(
        'planning',
        endpoint
      );

      console.log('🗑️ Delete Response:', response);

      // Better response handling
      if (response === null || response === undefined) {
        throw new Error('La réponse de l\'API est vide. Vérifiez l\'endpoint et les logs du serveur.');
      }

      if (response.success) {
        showNotification('Succès', 'Note supprimée avec succès', 'success');
        reloadData();
        // Update locally
        const updatedNotes = notes.filter(n => n._id !== noteToDelete._id);

        if (onNotesUpdate) {
          onNotesUpdate(updatedNotes);
        }

        // Reset states AFTER success
        setNoteToDelete(null);
      } else {
        throw new Error(response.error || response.message || response.errors?.[0]?.msg || 'Échec de la suppression');
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      showNotification('Erreur', error.message || 'Échec de la suppression de la note', 'danger');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false); // Close popup after operation (success or error)
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setNoteToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Formater la date
  const getNoteTime = (note) => {
    if (note.updatedAt) {
      return `Modifié le ${formatDateTime ? formatDateTime(note.updatedAt) : new Date(note.updatedAt).toLocaleDateString('fr-FR')}`;
    }
    return formatDateTime ? formatDateTime(note.createdAt) : new Date(note.createdAt).toLocaleDateString('fr-FR');
  };

  // Rendu du formulaire de création de note
  const renderCreateNoteForm = () => (
    <Card className="border shadow-sm mb-4">
      <Card.Header className="bg-white py-3 border-bottom">
        <h5 className="mb-0 fw-bold d-flex align-items-center text-dark">
          <Icon icon="AddComment" className="me-2 text-primary" />
          Ajouter une nouvelle note
        </h5>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Label className="text-muted small">Votre note</Form.Label>
          <Form.Control
            ref={textareaRef}
            as="textarea"
            rows={4}
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            placeholder="Écrivez votre note ici..."
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Vous pouvez formater votre texte avec des retours à la ligne. Les notes sont visibles par tous les utilisateurs.
          </Form.Text>
        </Form.Group>

        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleSaveNewNote}
            disabled={loading || !newNoteText.trim()}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Enregistrement...
              </>
            ) : (
              <>
                <Icon icon="Save" className="me-2" />
                Enregistrer la note
              </>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={handleCancelCreate}
            disabled={loading}
          >
            Annuler
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  // Rendu de chaque note
  const renderNoteItem = (note, index) => {
    const displayName = getUserDisplayName(note.createdBy) || 'Utilisateur';
    const time = getNoteTime(note);
    const isEditable = canEditNote(note);
    const isEditing = editingNote?._id === note._id;
    const hasBeenEdited = note.updatedAt && note.createdAt !== note.updatedAt;

    return (
      <div key={note._id || index} className="py-3 px-4 border-bottom position-relative">
        <div className="d-flex align-items-start">
          {/* Avatar */}
          <div className="bg-white border text-primary rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: 32, height: 32 }}>
            <Icon icon="Person" size="sm" />
          </div>

          {/* Content */}
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between mb-1">
              <div className="d-flex align-items-center">
                <strong className="text-dark">{displayName}</strong>
                {note.type === 'system' && (
                  <span className="badge bg-info ms-2">Système</span>
                )}
                {note.priority === 'high' && (
                  <span className="badge bg-danger ms-2">Important</span>
                )}
              </div>
              <small className="text-muted d-flex align-items-center">
                <Icon icon={hasBeenEdited ? "Edit" : "AccessTime"} size="xs" className="me-1" />
                {time}
                {hasBeenEdited && (
                  <span className="ms-1 text-info" title="Cette note a été modifiée">
                    (modifié)
                  </span>
                )}
              </small>
            </div>

            {isEditing ? (
              <div className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="mb-2"
                  disabled={loading}
                  autoFocus
                />
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={loading || !editText.trim()}
                  >
                    {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setEditingNote(null);
                      setEditText('');
                    }}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-secondary" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>
                {note.note || <em>Aucune note</em>}
              </div>
            )}
          </div>

          {/* Actions (seulement si l'utilisateur peut éditer) */}
          {isEditable && !isEditing && (
            <div className="d-flex gap-1 ms-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleEditClick(note)}
                title="Modifier"
                disabled={loading}
              >
                <Icon icon="Edit" size="xs" />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteClick(note)}
                title="Supprimer"
                disabled={loading}
              >
                <Icon icon="Delete" size="xs" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Si pas d'ID d'intervention
  if (!interventionId) {
    return (
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-white py-3 border-bottom">
          <h5 className="mb-0 fw-bold d-flex align-items-center text-dark">
            <Icon icon="Notes" className="me-2 text-primary" />
            Notes & Commentaires
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            <Icon icon="Warning" className="me-2" />
            L'ID d'intervention est manquant. Les notes sont en lecture seule.
          </Alert>
          {notes && notes.length > 0 ? (
            notes.map((note, index) => {
              const displayName = getUserDisplayName(note.createdBy) || 'Utilisateur';
              const time = getNoteTime(note);

              return (
                <div key={note._id || index} className="py-3 border-bottom">
                  <div className="d-flex justify-content-between mb-1">
                    <strong>{displayName}</strong>
                    <small className="text-muted">{time}</small>
                  </div>
                  <div className="text-secondary" style={{ whiteSpace: 'pre-wrap' }}>
                    {note.note}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-muted text-center py-4 mb-0">Aucune note</p>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {/* Create New Note Form */}
      {!isTechnician && isCreating && renderCreateNoteForm()}

      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-white py-3 border-bottom">
          <div className="d-flex justify-content-between align-items-center w-100 mb-3">
            {/* Left Side: Title & Badge */}
            <h5 className="mb-0 fw-bold d-flex align-items-center text-dark">
              <Icon icon="Notes" className="me-2 text-primary" />
              Notes & Commentaires
              {notes && notes.length > 0 && (
                <span className="badge bg-secondary ms-2">{notes.length}</span>
              )}
            </h5>

            {/* Right Side: Action Button */}
            {!isTechnician && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleStartCreate}
                disabled={isCreating || loading}
                className="ms-auto" // Pushes to the right as a safety backup
              >
                <Icon icon="Add" className="me-1" />
                Ajouter une note
              </Button>
            )}
          </div>
        </Card.Header>

        {(!notes || notes.length === 0) ? (
          <Card.Body className="text-center py-5">
            <Icon icon="StickyNote2" size="lg" className="text-muted mb-3" style={{ fontSize: '3rem' }} />
            <h6 className="text-muted mb-2">Aucune note pour cette intervention</h6>
            <p className="text-muted small mb-3">
              Soyez le premier à ajouter une note ou un commentaire.
            </p>
            <Button
              variant="outline-primary"
              onClick={handleStartCreate}
              disabled={isCreating || loading}
            >
              <Icon icon="AddComment" className="me-2" />
              Ajouter la première note
            </Button>
          </Card.Body>
        ) : (
          <Card.Body className="p-0">
            {notes.map((note, index) => renderNoteItem(note, index))}
          </Card.Body>
        )}
      </Card>

      {/* Confirmation Popup pour la suppression */}
      {showDeleteConfirm && noteToDelete && (
        <ConfirmationPopup
          setOpen={setShowDeleteConfirm}
          title="Supprimer la note ?"
          message={
            <div>
              <p className="mb-3">
                Êtes-vous sûr de vouloir supprimer cette note ?
              </p>

              <div className="alert alert-warning mb-2">
                <Icon icon="Warning" className="me-2" />
                <strong>Contenu de la note :</strong>
                <div className="mt-2 p-2 bg-light rounded" style={{ maxHeight: '150px', overflow: 'auto' }}>
                  {noteToDelete.note || '(note vide)'}
                </div>
              </div>

              <div className="alert alert-danger mb-0">
                <Icon icon="Info" className="me-2" />
                Cette action est irréversible. La note sera définitivement supprimée.
              </div>
            </div>
          }
          handleConfirm={handleDeleteNote}
          handleCancel={handleCancelDelete}
          confirmText={loading ? 'Suppression...' : 'Supprimer'}
          cancelText="Annuler"
          confirmColor="danger"
          disabled={loading}
          isCentered={true}
        />
      )}
    </>
  );
};

export default NotesSection;